import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { MessageSquare, RefreshCw, Download, Search, Filter } from 'lucide-react';
import { useWhatsAppMessages } from '@/hooks/useWhatsAppMessages';
import { WhatsAppMessageCard } from './WhatsAppMessageCard';

export const WhatsAppMessageHistory: React.FC = () => {
  const [filters, setFilters] = useState<{
    direction?: 'INCOMING' | 'OUTGOING';
    type?: string;
    startDate?: string;
    endDate?: string;
    limit: number;
  }>({
    limit: 50,
  });

  const [searchTerm, setSearchTerm] = useState('');

  const { data: messages, isLoading, refetch } = useWhatsAppMessages(filters);

  const handleExportCSV = () => {
    if (!messages || messages.length === 0) return;

    const headers = ['Timestamp', 'Direction', 'Type', 'Phone Number', 'Content', 'Lead', 'User', 'Status'];
    const rows = messages.map((msg) => [
      new Date(msg.timestamp).toLocaleString(),
      msg.direction,
      msg.type,
      msg.phoneNumber,
      msg.content.replace(/"/g, '""'), // Escape quotes
      msg.lead ? `${msg.lead.name} (${msg.lead.company})` : '',
      msg.user ? msg.user.name : '',
      msg.status || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `whatsapp-messages-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredMessages = messages?.filter((msg) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      msg.content.toLowerCase().includes(searchLower) ||
      msg.phoneNumber.includes(searchLower) ||
      msg.lead?.name.toLowerCase().includes(searchLower) ||
      msg.lead?.company.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <MessageSquare className="h-5 w-5" />
            <span>Message History</span>
          </CardTitle>
          <div className="flex items-center space-x-2">
            <Button onClick={handleExportCSV} variant="outline" size="sm" disabled={!messages || messages.length === 0}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={() => refetch()} variant="outline" size="sm" disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Direction Filter */}
            <Select
              value={filters.direction}
              onValueChange={(value) => setFilters((prev) => ({ ...prev, direction: value === '' ? undefined : value as 'INCOMING' | 'OUTGOING' }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Directions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Directions</SelectItem>
                <SelectItem value="INCOMING">Incoming</SelectItem>
                <SelectItem value="OUTGOING">Outgoing</SelectItem>
              </SelectContent>
            </Select>

            {/* Type Filter */}
            <Select
              value={filters.type}
              onValueChange={(value) => setFilters((prev) => ({ ...prev, type: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Types</SelectItem>
                <SelectItem value="MANUAL">Manual</SelectItem>
                <SelectItem value="AUTO">Auto</SelectItem>
                <SelectItem value="WELCOME">Welcome</SelectItem>
                <SelectItem value="REMINDER">Reminder</SelectItem>
                <SelectItem value="FOLLOW_UP">Follow-up</SelectItem>
                <SelectItem value="PROPOSAL">Proposal</SelectItem>
                <SelectItem value="NEGOTIATION">Negotiation</SelectItem>
              </SelectContent>
            </Select>

            {/* Start Date */}
            <Input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value }))}
              placeholder="Start Date"
            />

            {/* End Date */}
            <Input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value }))}
              placeholder="End Date"
            />
          </div>

          {/* Clear Filters */}
          {(filters.direction || filters.type || filters.startDate || filters.endDate || searchTerm) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFilters({ limit: 50 });
                setSearchTerm('');
              }}
            >
              <Filter className="h-4 w-4 mr-2" />
              Clear Filters
            </Button>
          )}

          {/* Messages List */}
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {isLoading ? (
              <div className="text-center py-8">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                <p className="text-sm text-gray-500 mt-2">Loading messages...</p>
              </div>
            ) : filteredMessages && filteredMessages.length > 0 ? (
              filteredMessages.map((message) => (
                <WhatsAppMessageCard key={message.id} message={message} />
              ))
            ) : (
              <div className="text-center py-12">
                <MessageSquare className="h-12 w-12 mx-auto text-gray-300" />
                <p className="text-gray-500 mt-3">No messages found</p>
                <p className="text-sm text-gray-400 mt-1">
                  {filters.direction || filters.type || searchTerm
                    ? 'Try adjusting your filters'
                    : 'Messages will appear here once sent or received'}
                </p>
              </div>
            )}
          </div>

          {/* Pagination Info */}
          {filteredMessages && filteredMessages.length > 0 && (
            <div className="text-sm text-gray-500 text-center pt-4 border-t">
              Showing {filteredMessages.length} message{filteredMessages.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
